import { send } from "@utils/fetch";
import {
  LabData,
  PatientIntakeSessionStorage,
  PharmacyAddress,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/usePatientIntakeSessionStorage";
import {
  APPOINTMENT_DURATION_MINUTES,
  getLocalFormattedStartDate,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/dateUtils";
import { ConsultType } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import {
  ConsultDisposition,
  ConsultStatus,
} from "src/funnels/insurance-reimbursement/constants";
import { genderConstants } from "src/utils/sexAndGender";
import getStore from "src/utils/redux/store";
import { getSessionState } from "@pageDefinitions/session";

// Generalize intake to include everything we want without having to super
// extend the PatientIntakeSessionStorage keys
type IntakeToSend = {
  [key: string]:
    | string
    | string[]
    | LabData
    | number
    | PharmacyAddress
    | boolean;
};

export const postPatientIntakeSteady = (
  userId: string,
  patientIntake: PatientIntakeSessionStorage,
  intakeFinalized = false
): Promise<JsonObject> => {
  // PatientIntake survey responses already loaded into patientIntake param
  const body: {
    patientIntake: IntakeToSend;
    consultType: ConsultType;
    intakeFinalized: boolean;
  } = {
    patientIntake,
    consultType: ConsultType.VIDEO,
    intakeFinalized,
  };

  const url = `/api/insurance/v2/patient_intake/${userId}/`;
  return send("POST", url, body);
};

const getIsRescheduleFromWindowLocation = () =>
  window.location.pathname.startsWith("/telehealth/reschedule");

export const postPatientIntake = (
  userId: string,
  patientIntake: PatientIntakeSessionStorage
): Promise<JsonObject> => {
  const body = {
    patientIntake,
    consultType: getSessionState("browser").consultTypeToSchedule,
  };

  // Set eating disorder response if in survey answers
  if (getStore().getState().surveyAnswers.eatingDisorder?.[0]) {
    body.patientIntake.eatingDisorder =
      getStore().getState().surveyAnswers.eatingDisorder?.[0];
  }

  // In case we skipped this question, add the answer "no" to the intake
  const gender =
    getStore().getState().surveyAnswers.insuranceGender?.[0] ||
    getStore().getState().surveyAnswers?.gender?.[0];
  if (gender === genderConstants.male) {
    body.patientIntake.pregnantOrBreastfeeding = "no";
  }

  const url = `/api/insurance/v5/patient_intake/${userId}/`;
  return send("POST", url, body);
};

// On success, we get an empty response back
type ScheduleAndFinalizeValidResponse = Record<string, never>;

type ScheduleAndFinalizeInvalidResponse = {
  error: string;
};

export type ScheduleAndFinalizeResponse =
  | ScheduleAndFinalizeValidResponse
  | ScheduleAndFinalizeInvalidResponse;

export const postScheduleAndFinalize = (
  userId: string,
  wheelConsultId: string,
  workflow: string,
  consultType: ConsultType,
  start: string,
  preferredPharmacyId: string,
  desiredClinicianId: string,
  labData: LabData
): Promise<ScheduleAndFinalizeResponse> => {
  const startFormatted = getLocalFormattedStartDate(start);
  const isReschedule = getIsRescheduleFromWindowLocation();
  const appointmentOffset = 0;
  const appointmentDuration = APPOINTMENT_DURATION_MINUTES;
  const url = "/api/insurance/v2/schedule_appointment_and_finalize_consult/";
  return send("POST", url, {
    userId,
    start,
    appointmentDuration,
    appointmentOffset,
    wheelConsultId,
    workflow,
    consultType,
    startFormatted,
    isReschedule,
    preferredPharmacyId,
    desiredClinicianId,
    labData,
  });
};

export const postMakeEligible = (userId: string): Promise<JsonObject> => {
  const url = "/api/insurance/v1/make_eligible_for_reimbursement/";
  return send("POST", url, { userId });
};

export const enrollPatient = (
  userId: string,
  accessCode?: string
): Promise<JsonObject> => {
  const url = "/api/insurance/v1/enroll_patient/";
  return send("POST", url, { userId, accessCode });
};

export const createSteadyPatient = (payload: any): Promise<JsonObject> => {
  const url = "/api/insurance/v1/create_steady_patient/";
  return send("POST", url, payload);
};

export type SyncConsultResponse = {
  consultGuid: string;
  timeslotStartUtc: string;
  timeslotEndUtc: string;
  timeZone: string;
  canCancel?: boolean;
};

export const createSyncVideoConsult = (
  userId: string,
  isReschedule = false,
  stateCode?: string,
  preferredClinicianId?: string
): Promise<SyncConsultResponse> => {
  const url = `/api/insurance/v1/patient/${userId}/consult/video/`;
  return send("POST", url, {
    isReschedule,
    stateCode,
    steadyClinicianId: preferredClinicianId,
  });
};

export type AvailableAppointmentsResponse = {
  availability: string[];
};

export const getSyncVideoConsultAvailability = (
  consultGuid: string
): Promise<AvailableAppointmentsResponse> => {
  const url = `/api/insurance/v1/consult/${consultGuid}/available_appointments/`;
  return send("GET", url);
};

export const getAvailabilityForAppointment = (
  userId: string,
  stateCode?: string,
  clinician_id?: string
): Promise<AvailableAppointmentsResponse> => {
  const params = new URLSearchParams();
  if (stateCode) {
    params.append("stateCode", stateCode);
  }
  if (clinician_id) {
    params.append("clinicianId", clinician_id);
  }

  const url = `/api/insurance/v1/patient/${userId}/available_appointments/`;
  return send("GET", url, null, { params });
};

export const scheduleAppointment = (
  consultGuid: string,
  startsAt: string,
  timezone: string
): Promise<SyncConsultResponse> => {
  const url = `/api/insurance/v1/consult/${consultGuid}/schedule_appointment/`;
  return send("POST", url, { starts_at: startsAt, timezone });
};

export const scheduleAppointmentWithoutConsultGuid = (
  userId: string,
  startsAt: string,
  timezone: string,
  isTelehealthBuyflow: boolean,
  stateCode?: string,
  clinicianId?: string
): Promise<SyncConsultResponse> => {
  const url = `/api/insurance/v1/patient/${userId}/schedule_appointment/`;
  return send("POST", url, {
    startsAt,
    timezone,
    stateCode,
    clinicianId,
    isTelehealthBuyflow,
  });
};

export type ClinicianResponse = {
  clinician: {
    first_name: string;
    last_name: string;
    title: string;
    id: string;
  };
};

export const getConsultClinician = (
  consultGuid: string
): Promise<ClinicianResponse> => {
  const url = `/api/insurance/v1/consult/${consultGuid}/clinician/`;
  return send("GET", url);
};

export type ListPharmacy = {
  storeName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  primaryPhone: string;
  primaryFax: string;
  primarySpeciality: string[];
};

export type ListPharmaciesResponse = {
  pharmacies: ListPharmacy[];
};

export const getPharmaciesAddresses = (
  zipCode?: string,
  name?: string
): Promise<ListPharmaciesResponse> => {
  const params = new URLSearchParams();
  if (zipCode) {
    params.append("zipcode", zipCode);
  }
  if (name) {
    params.append("name", name);
  }
  const url = `/api/insurance/v2/pharmacies/?`;
  return send("GET", url, null, { params });
};

export const savePreferredPharmacy = (
  userId: string,
  pharmacy: PharmacyAddress
): Promise<object> => {
  const url = `/api/insurance/v1/patient/${userId}/pharmacy/`;
  return send("PATCH", url, { pharmacy });
};

export type CreateConsultAndGetTimeslotsResponse = {
  wheelConsultId: string;
  workflow: string;
  consultType: ConsultType;
  timeslots: string[];
  clinician_names: Record<string, string>;
};

export const postCreateConsultAndGetTimeslots = (
  userId: string,
  clinicianId?: string,
  lookaheadDays?: number,
  consultType?: ConsultType
): Promise<CreateConsultAndGetTimeslotsResponse> => {
  const isReschedule = getIsRescheduleFromWindowLocation();
  return send(
    "POST",
    "/api/insurance/v2/create_consult_and_get_timeslots/",
    omitBy(
      {
        userId,
        isReschedule,
        clinicianId,
        lookaheadDays,
        consultType,
      },
      isNil
    )
  );
};

export type PatientIntakeQuestion = {
  questionId: string;
  shouldAsk: boolean;
  config: { [key: string]: any };
};

export type RequiredPatientIntakeQuestionsResponse = {
  questions: PatientIntakeQuestion[];
  hasOpenDraftIntake?: boolean;
};

export const getRequiredPatientIntakeQuestions = (
  userId: string,
  consultType: ConsultType
): Promise<RequiredPatientIntakeQuestionsResponse> => {
  const params = new URLSearchParams();
  params.append("userId", userId);
  params.append("consultType", consultType);
  return send("GET", "/api/insurance/v1/required_patient_intake/", null, {
    params,
  });
};

export const finalizeAllOpenIntakes = (userId: string) => {
  return send(
    "POST",
    `/api/insurance/v1/patient/${userId}/finalize_all_open_intakes/`,
    {}
  );
};

export enum PatientType {
  STEADY = "steadymd",
  WHEEL = "wheel",
}

type PatientInfo = {
  hasPatientIntake: boolean;
  isFinalizedConsultInFuture: boolean;
  hasConsult: false;
  nextConsultType: ConsultType;
  hasActiveInsurance: boolean;
  hasHealthyWeightPlan?: boolean;
  patientType: PatientType;
  stateCode: string;
  telehealthSignupDate: string;
  hasSubmittedIntake?: boolean;
  insuranceCoverage?: "yes" | "no";
};

export type LatestConsultInfo = Omit<PatientInfo, "hasConsult"> & {
  hasConsult: true;
  // Note: For RPM type consults this timeslotLocalFormatted is 5 minutes ahead of the actual appointment start time
  timeslotLocalFormatted: string;
  wheelConsultId: string;
  consultType: ConsultType;
  clinicianId: string;
  doxyUrl: string;
};

export type GetLatestConsultInfoResponse = PatientInfo | LatestConsultInfo;

export const getLatestConsultInfo = (
  userId: string,
  consult_status_filter?: ConsultStatus,
  consult_disposition_filter?: ConsultDisposition
): Promise<GetLatestConsultInfoResponse> => {
  const params = new URLSearchParams();

  if (consult_status_filter) {
    params.append("consult_status_filter", consult_status_filter);
  }

  if (consult_disposition_filter) {
    params.append("consult_disposition_filter", consult_disposition_filter);
  }
  return send(
    "GET",
    `/api/insurance/v1/latest_consult_info/${encodeURIComponent(userId)}/`,
    null,
    { params }
  );
};

export type LatestVideoVisit = {
  hasConsult: boolean;
  timeslotLocalFormatted?: string; // Will be null when consult is waiting_to_schedule
  clinicianId: string;
  doxyUrl: string;
  consultGuid: string;
  consultStatus: string;
  canCancel?: boolean;
};

export const cancelAppointmentByGuid = (guid: string) => {
  return send(
    "POST",
    `/api/insurance/v1/consult/${encodeURIComponent(guid)}/cancel/`
  );
};

export const getLatestSteadyConsultInfo = (
  userId: string,
  consultStatusFilter?: ConsultStatus,
  consultStatusExcludeFilter?: ConsultStatus[]
): Promise<LatestVideoVisit> => {
  const params = new URLSearchParams();

  if (consultStatusFilter) {
    params.append("consult_status_filter", consultStatusFilter);
  }

  if (consultStatusExcludeFilter) {
    params.append(
      "exclude_consult_status_filter",
      consultStatusExcludeFilter.join(",")
    );
  }

  return send(
    "GET",
    `/api/insurance/v1/patient/${encodeURIComponent(
      userId
    )}/consult/video/latest/`,
    null,
    { params }
  );
};

export const getLatestUpcomingStatusConsultInfo = (userId: string) => {
  return getLatestSteadyConsultInfo(userId, null, [
    ConsultStatus.WAITING_TO_SCHEDULE,
    ConsultStatus.REJECTED,
    ConsultStatus.COMPLETED,
    ConsultStatus.CANCELED,
    ConsultStatus.REFERRED_OUT,
  ]);
};

export type WheelClinician = {
  clinician_name: string;
  practitioner_type: string;
  doxy_url: string;
  clinician_id: string;
};

export const getConsultClinicianInfo = (
  wheelConsultId: string
): Promise<WheelClinician> =>
  send(
    "GET",
    `/api/insurance/v1/get_consult_clinician_info/${wheelConsultId}/`
  );

export enum ConsentType {
  PRIVACY = "PRIVACY",
  TELEHEALTH_INFORMED_CONSENT = "TELEHEALTH_INFORMED_CONSENT",
  RPM_COPAYS = "RPM_COPAYS",
  SMS = "SMS",
}

export type Consent = {
  consent_type: ConsentType;
  consent_status: boolean;
  consent_message: string;
  meristem_sha: string;
  url_to_file: string;
  ip_address?: string;
};

export const postConsentsForTelehealth = (
  user_id: string,
  consent_payloads: Consent[]
): Promise<JsonObject> => {
  const url = `/api/insurance/v1/consent/${user_id}/`;
  return send("POST", url, {
    data: consent_payloads,
  });
};

export type GetConsentForUserResponse = {
  date_consented?: string;
  consent_status?: boolean;
};

export const getConsentForUser = (
  user_id: string,
  consent_type: ConsentType
): Promise<GetConsentForUserResponse> => {
  const url = `/api/insurance/v1/consent/${user_id}/?type=${consent_type}`;
  return send("GET", url);
};

export type GetPhoneNumberResponse = {
  phone_code: string;
};

export const getPhoneCodeForUser = (
  user_id: string
): Promise<GetPhoneNumberResponse> => {
  const url = `/api/insurance/v1/get_phone_code/${user_id}/`;
  return send("GET", url);
};

export type UploadLabsResponse = {
  data: {
    url: string;
    contentType: string;
    title: string;
  }[];
};
export const uploadLabs = (formData: FormData): Promise<UploadLabsResponse> => {
  return send("POST", "/api/insurance/v1/documents/upload/", formData);
};

export type Pharmacy = {
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  };
  dose_spot_id: string;
};

export type GetPharmaciesResponse = {
  data: Pharmacy[];
};

export const getPharmacies = (
  postalCode: string,
  pharmacyName = ""
): Promise<GetPharmaciesResponse> => {
  let get_pharmacies_route = `/api/insurance/v1/pharmacies/?postal_code=${postalCode}`;
  if (pharmacyName) {
    get_pharmacies_route = `${get_pharmacies_route}&pharmacy_name=${pharmacyName}`;
  }
  return send("GET", get_pharmacies_route);
};

export type GetProviderIDResponse = {
  providerId: string;
  state: string;
};

export const getProviderID = (
  userId: string
): Promise<GetProviderIDResponse> => {
  const url = `/api/insurance/v1/get_provider_id/${userId}/`;
  return send("GET", url);
};

export const sendTelehealthFeedbackSurveyResponse = (
  wheelConsultId: string,
  surveyVersionName: string,
  surveyAnswers: object
) => {
  const url = `/api/insurance/v1/feedback/consult/${wheelConsultId}/`;
  return send("PATCH", url, { surveyVersionName, surveyAnswers });
};

export type OrderLabsPayload = {
  paymentResponsibility: PaymentResponsibility;
  laboratory: string;
  productId: string;
};

export const orderLabs = (
  userId: string,
  payload: OrderLabsPayload
): Promise<JsonObject> => {
  const url = `/api/insurance/v1/patient/${userId}/consult/labs/`;
  return send("POST", url, { ...payload });
};

export const getUserType = (
  userId: string
): Promise<{ patientType: PatientType }> => {
  return send(
    "GET",
    `/api/insurance/v1/patient/${encodeURIComponent(userId)}/type/`
  );
};

export const uploadSteadyLabs = (
  userId,
  formData: FormData
): Promise<UploadLabsResponse> => {
  return send(
    "POST",
    `/api/insurance/v1/patient/${userId}/upload_labs/`,
    formData
  );
};

export type HomeAddress = {
  address1: string;
  address2: string;
  city: string;
  usState: string;
  zipCode: string;
};

export const getHomeAddress = (userId: string): Promise<HomeAddress> => {
  return send(
    "GET",
    `/api/insurance/v1/patient/${encodeURIComponent(userId)}/home_address/`
  );
};

// self     = patient pays directly (we charge them)
// patient  = patient pays via insurance
// the other two are not used, but are possible options in steady docs
export type PaymentResponsibility =
  | "self"
  | "guarantor"
  | "patient"
  | "thirdParty";

export type LabOrder = {
  created: string;
  guid: string;
  laboratory: string;
  paymentResponsibility: PaymentResponsibility;
};

export const getLabOrder = (userId: string): Promise<LabOrder> => {
  return send(
    "GET",
    `/api/insurance/v1/patient/${encodeURIComponent(userId)}/consult/lab_order/`
  );
};
